/**
 * AccouPro — Industry-Based Automatic Accounting Setup Engine
 * 19 comprehensive, production-grade industry templates with specialized
 * inventory groups, measurement units, direct/indirect expense accounts,
 * sales/purchase ledgers, stock valuation, and tax mappings.
 */

export interface LedgerTemplateItem {
  name: string;
  group: string;
  nature: "Asset" | "Liability" | "Equity" | "Income" | "Expense";
  type: "Dr" | "Cr";
  description?: string;
  isOptional?: boolean;
}

export interface IndustryTemplate {
  id: string;
  name: string;
  badge: string;
  description: string;
  icon: string;
  units: string[];
  defaultGSTRate: number;
  valuationMethod: "FIFO" | "Weighted Average" | "Retail Method" | "Standard Costing" | "Specific Identification";
  inventoryGroups: string[];
  inventoryCategories: string[];
  incomeLedgers: LedgerTemplateItem[];
  purchaseLedgers: LedgerTemplateItem[];
  directExpenses: LedgerTemplateItem[];
  indirectExpenses: LedgerTemplateItem[];
  popularHSN: { code: string; desc: string; rate: number }[];
  nextSteps: { title: string; desc: string; actionUrl: string; icon: string }[];
}

const RAW_INDUSTRY_TEMPLATES: IndustryTemplate[] = [
  {
    id: "iron_steel",
    name: "Iron and Steel Trading",
    badge: "Metals & Industrial",
    description: "TMT bars, structural steel, plates, pipes, angles, channels, weight-based (MT/KG) accounting.",
    icon: "Hammer",
    units: ["MT", "TON", "KG", "QUINTAL", "PCS", "BUNDLE"],
    defaultGSTRate: 18,
    valuationMethod: "Weighted Average",
    inventoryGroups: [
      "TMT Rebars & Rods",
      "Steel Plates & Sheets",
      "Pipes & Hollow Sections",
      "Angles, Channels & Beams",
      "Binding Wire & Wire Mesh",
      "Flanges & Industrial Fittings"
    ],
    inventoryCategories: ["Structural Steel", "Long Products", "Flat Products", "Fabricated Steel"],
    incomeLedgers: [
      { name: "Sales - TMT & Structural Steel", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Steel Pipes & Tubes", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Cutting & Processing Charges Received", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Primary Steel (MMS/SAIL/Tata)", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Secondary Steel & Re-rollers", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Freight Inward (Truck & Trailer)", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Transport of heavy steel consignments" },
      { name: "Loading & Unloading Charges (Hamali)", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Yard crane and labour charges" },
      { name: "Weighbridge (Dharmakanta) Charges", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Gross and tare weight certification" },
      { name: "Crane & Hydra Rental", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Heavy material shifting equipment" },
      { name: "Cut-off & Scrap Losses", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Processing and cut-to-length loss" },
    ],
    indirectExpenses: [
      { name: "Steel Yard Ground Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Yard Security & Watchman Wages", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Vehicle Transit Insurance", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "7214", desc: "TMT Rebars and Iron Rods", rate: 18 },
      { code: "7208", desc: "Flat-rolled Steel Plates", rate: 18 },
      { code: "7306", desc: "Iron & Steel Pipes / Tubes", rate: 18 },
      { code: "7216", desc: "Angles, Shapes & Sections", rate: 18 }
    ],
    nextSteps: [
      { title: "Set Up Steel Yard & Warehouses", desc: "Configure physical stock yards or Godowns for weight tracking", actionUrl: "/dashboard/inventory", icon: "Warehouse" },
      { title: "Add Steel Mills & Vendors", desc: "Add suppliers like Tata Steel, JSW, or regional rolling mills", actionUrl: "/dashboard/parties", icon: "Truck" },
      { title: "Record Opening Steel Stock", desc: "Enter your initial stock in MT with purchase rates", actionUrl: "/dashboard/inventory", icon: "Package" },
      { title: "Create First Steel Sales Invoice", desc: "Generate a GST invoice with MT quantity and weighbridge slip reference", actionUrl: "/dashboard/sales", icon: "Receipt" }
    ]
  },
  {
    id: "scrap_metal",
    name: "Scrap and Metal Trading",
    badge: "Recycling & Metals",
    description: "Ferrous & non-ferrous scrap, brass, copper, battery scrap, RCM compliance & weight reconciliation.",
    icon: "Recycle",
    units: ["KG", "MT", "TON", "QUINTAL"],
    defaultGSTRate: 18,
    valuationMethod: "Weighted Average",
    inventoryGroups: [
      "Heavy Melting Steel Scrap (HMS 1 & 2)",
      "Cast Iron Scrap",
      "Copper Wire & Birch Scrap",
      "Aluminum Scrap & Sections",
      "Brass & Honey Scrap",
      "Lead & Battery Scrap"
    ],
    inventoryCategories: ["Ferrous Scrap", "Non-Ferrous Scrap", "Battery & E-Waste"],
    incomeLedgers: [
      { name: "Sales - Ferrous Scrap", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Non-Ferrous Scrap (Copper/Brass)", group: "Sales Accounts", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Scrap from Registered Dealers", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Scrap from Unregistered (RCM)", group: "Purchase Accounts", nature: "Expense", type: "Dr", description: "Reverse Charge Mechanism applicable on scrap purchases" },
    ],
    directExpenses: [
      { name: "Sorting & Segregation Labour", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Manual sorting of mixed metal scrap" },
      { name: "Gas Cutting & Baling Expenses", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Oxygen/LPG cylinder and scrap baling costs" },
      { name: "Cartage & Tractor Freight", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Weighbridge Charges", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Melting / Dross / Burning Loss", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Scrap Yard Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Pollution Control Board & Environmental Fee", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "7204", desc: "Ferrous Waste and Scrap", rate: 18 },
      { code: "7404", desc: "Copper Waste and Scrap", rate: 18 },
      { code: "7602", desc: "Aluminum Waste and Scrap", rate: 18 }
    ],
    nextSteps: [
      { title: "Review Scrap RCM Tax Settings", desc: "Ensure Reverse Charge is mapped for unregistered purchases", actionUrl: "/dashboard/gst", icon: "ShieldCheck" },
      { title: "Add Scrap Suppliers & Kabadiwalas", desc: "Keep supplier records categorized by metal type", actionUrl: "/dashboard/parties", icon: "Users" },
      { title: "Record Metal Inward Stock", desc: "Enter KG/MT quantities for copper, brass, and iron scrap", actionUrl: "/dashboard/inventory", icon: "Package" }
    ]
  },
  {
    id: "grocery_kirana",
    name: "Grocery and Kirana Store",
    badge: "Retail & FMCG",
    description: "Grains, edible oils, pulses, packaged FMCG, daily staples with loose & packet units.",
    icon: "ShoppingBag",
    units: ["KG", "GRAM", "LTR", "ML", "PACKET", "PCS", "BOX", "BAG", "DOZEN"],
    defaultGSTRate: 5,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Grains, Rice & Wheat Flours",
      "Pulses, Dals & Legumes",
      "Edible Oils, Ghee & Vanaspati",
      "Spices, Salt & Sugar",
      "Packaged Foods, Biscuits & Snacks",
      "Beverages, Tea & Coffee",
      "Cleaning, Detergents & Home Care"
    ],
    inventoryCategories: ["Staples & Foodgrains", "Packaged FMCG", "Personal Care & Cleaning"],
    incomeLedgers: [
      { name: "Sales - Groceries & Provisions", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Packaged FMCG Goods", group: "Sales Accounts", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Wholesale Grains & Dals", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - FMCG Distributors", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Mandi Cess & Market Yard Fee", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Packaging Bags & Pouches", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Goods Inward Freight & Auto-Rickshaw", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Spoilage & Pest Damage", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Shop Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Electricity & Refrigerator Running", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Delivery Boy & Helper Wages", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "1006", desc: "Rice & Paddy", rate: 5 },
      { code: "1512", desc: "Refined Sunflower / Mustard Oil", rate: 5 },
      { code: "1905", desc: "Biscuits & Bakery Products", rate: 18 }
    ],
    nextSteps: [
      { title: "Set Up Fast-Selling Items", desc: "Add top 20 staples and grocery lines for quick billing", actionUrl: "/dashboard/inventory", icon: "PlusCircle" },
      { title: "Enable Quick POS / Barcode Billing", desc: "Configure sales invoice shortcuts for counter customers", actionUrl: "/dashboard/sales", icon: "Receipt" }
    ]
  },
  {
    id: "cosmetics_beauty",
    name: "Cosmetics and Beauty Products",
    badge: "Beauty & Personal Care",
    description: "Skincare, haircare, makeup, luxury fragrances, salon supplies, MRP tracking and brand accounts.",
    icon: "Sparkles",
    units: ["PCS", "BOTTLE", "TUBE", "PACK", "SET", "BOX", "DOZEN"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Skincare, Serums & Sunscreens",
      "Haircare, Shampoos & Colours",
      "Color Cosmetics & Makeup",
      "Perfumes, Deodorants & Fragrances",
      "Bath, Body & Hygiene",
      "Professional Salon Supplies & Tools"
    ],
    inventoryCategories: ["Premium Skincare", "Color Makeup", "Haircare", "Fragrances"],
    incomeLedgers: [
      { name: "Sales - Cosmetics & Skincare", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Perfumes & Luxury Brands", group: "Sales Accounts", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Beauty Brands & Super Stockists", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Brand Testers & Sample Costs", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Luxury Gift Packaging & Ribbons", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Express Courier & Fragile Inward Transit", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Showroom Rent & Display Counter Hire", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Air Conditioning Electricity", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Beauty Advisor Commission & Salary", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Social Media Adverts & Influencer Samples", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "3304", desc: "Skincare & Makeup Preparations", rate: 18 },
      { code: "3305", desc: "Haircare Preparations", rate: 18 },
      { code: "3307", desc: "Perfumes and Deodorants", rate: 28 }
    ],
    nextSteps: [
      { title: "Add Cosmetics Brands", desc: "Organize products by Brand (L'Oreal, Lakme, Minimalist, etc.)", actionUrl: "/dashboard/inventory", icon: "Tag" },
      { title: "Configure Retail Invoicing", desc: "Set up MRP billing with automatic discounts", actionUrl: "/dashboard/sales", icon: "Percent" }
    ]
  },
  {
    id: "electronics_accessories",
    name: "Electronics and Computer Accessories",
    badge: "Consumer Tech",
    description: "Laptops, phones, cables, chargers, PC parts, warranty & serial number tracking.",
    icon: "Laptop",
    units: ["PCS", "SET", "BOX", "NOS"],
    defaultGSTRate: 18,
    valuationMethod: "Specific Identification",
    inventoryGroups: [
      "Laptops, Desktops & Tablets",
      "Mobile Phones & Smartwatches",
      "Cables, Fast Chargers & Adapters",
      "Computer Peripherals (Keyboard, Mouse)",
      "Audio, Earbuds & Speakers",
      "Storage, SSDs & Flash Drives",
      "Networking Routers & Switches"
    ],
    inventoryCategories: ["Computing", "Mobile Tech", "Peripherals", "Networking"],
    incomeLedgers: [
      { name: "Sales - Electronics & Gadgets", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Repair & Service Labour Income", group: "Direct Incomes", nature: "Income", type: "Cr" },
      { name: "Extended Warranty Commission", group: "Indirect Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Tech Distributors (Ingram/Redington)", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Warranty Replacement & RMA Transit", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Technician & Assembly Labour", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Electronics Transit Insurance", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Store Rent & Security Cameras", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "E-Commerce Payment Gateway Charges", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Demo Units Depreciation", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "8471", desc: "Computers, Laptops & Processors", rate: 18 },
      { code: "8517", desc: "Mobile Phones & Cellular Spares", rate: 18 },
      { code: "8504", desc: "Power Adapters & Chargers", rate: 18 }
    ],
    nextSteps: [
      { title: "Track Serial & IMEI Numbers", desc: "Configure warranty and serial numbers for expensive tech lines", actionUrl: "/dashboard/inventory", icon: "Barcode" },
      { title: "Generate GST Tech Invoices", desc: "Ensure HSN codes 8471 and 8517 are prefilled", actionUrl: "/dashboard/sales", icon: "FileText" }
    ]
  },
  {
    id: "clothing_garments",
    name: "Clothing and Garments",
    badge: "Textiles & Fashion",
    description: "Men's, women's, kids wear, fabrics, dual slab GST (< ₹1,000 at 5%, > ₹1,000 at 12%), size-color variants.",
    icon: "Shirt",
    units: ["PCS", "METRE", "SET", "PAIR", "DOZEN"],
    defaultGSTRate: 12,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Men's Casual & Formal Wear",
      "Women's Ethnic, Sarees & Western",
      "Kids & Infants Apparel",
      "Dress Materials, Fabrics & Linings",
      "Innerwear & Loungewear",
      "Fashion Accessories & Belts"
    ],
    inventoryCategories: ["Men's Wear", "Women's Ethnic", "Kidswear", "Fabrics"],
    incomeLedgers: [
      { name: "Sales - Apparel (GST 5% / 12%)", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Stitching & Alteration Charges", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Readymade Garments", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Unstitched Fabrics & Mills", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Tailoring & Alteration Labour", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Garment Hangers, Tags & Carry Bags", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Ironing & Steam Press Expenses", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Apparel Showroom Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Display Mannequins & Lighting", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "End-of-Season Discount Allowed", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "6109", desc: "T-Shirts & Knitted Apparel", rate: 5 },
      { code: "6204", desc: "Women's Suits, Dresses & Sarees", rate: 12 },
      { code: "5208", desc: "Woven Cotton Fabrics", rate: 5 }
    ],
    nextSteps: [
      { title: "Configure Dual-Slab GST Rates", desc: "Verify automatic 5% vs 12% calculation based on piece price", actionUrl: "/dashboard/gst", icon: "Percent" },
      { title: "Organize Sizes & Colors", desc: "Group inventory items by Size (S, M, L, XL) and Color", actionUrl: "/dashboard/inventory", icon: "Layers" }
    ]
  },
  {
    id: "wholesale_distribution",
    name: "Wholesale Distribution",
    badge: "Distribution & B2B",
    description: "High-volume B2B distribution, carton/case units, target rebates, credit terms & transporter billing.",
    icon: "Truck",
    units: ["CARTON", "BOX", "CASE", "CRATE", "PALLET", "PCS", "DOZEN"],
    defaultGSTRate: 18,
    valuationMethod: "Weighted Average",
    inventoryGroups: [
      "Tier 1 FMCG Fast Lines",
      "Bulk Packed Merchandise",
      "Seasonal Distribution Stock",
      "Promotional Schemes Stock"
    ],
    inventoryCategories: ["Primary Distribution", "Regional Stock", "Promotional"],
    incomeLedgers: [
      { name: "Sales - B2B Wholesale", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Company Target Incentive & Rebate", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Direct Manufacturer Inward", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Warehouse Inward Truck Freight", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Godown Hamali & Stacking Charges", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Local Route Delivery Vehicle Expenses", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Central Godown Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Salesmen Daily Allowances & Payouts", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Bad Debts Written Off", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "2106", desc: "Food Preparations & Edibles", rate: 18 },
      { code: "3402", desc: "Washing & Cleaning Agents", rate: 18 }
    ],
    nextSteps: [
      { title: "Define Retailer Credit Limits", desc: "Set credit periods (7, 15, 30 days) for dealer accounts", actionUrl: "/dashboard/parties", icon: "Clock" },
      { title: "Generate Bulk Tax Invoices with E-Way Bill", desc: "Issue B2B invoices with transporter details", actionUrl: "/dashboard/invoices", icon: "Truck" }
    ]
  },
  {
    id: "retail_shop",
    name: "Retail Shop",
    badge: "General Retail",
    description: "General retail counter, POS, fast walk-in cash and UPI payments, daily registers.",
    icon: "Store",
    units: ["PCS", "PACK", "BOX", "BOTTLE", "NOS"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Front Counter Fast Movers",
      "Aisle Merchandise",
      "Impulse Buying Items",
      "Backstore Surplus"
    ],
    inventoryCategories: ["General Goods", "Convenience Lines"],
    incomeLedgers: [
      { name: "Sales - Retail Walk-in Counter", group: "Sales Accounts", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Retail Stock", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Carry Bags & Printing", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Transport & Porter Charges", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Shop Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Shop Electricity & Power Backup", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "POS & QR Code MDR Charges", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Sales Counter Staff Wages", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "3924", desc: "Household & Table Articles", rate: 18 }
    ],
    nextSteps: [
      { title: "Add Retail Products", desc: "List your core store products with selling prices", actionUrl: "/dashboard/inventory", icon: "Plus" },
      { title: "Bill Your First Customer", desc: "Generate a simplified POS receipt", actionUrl: "/dashboard/sales", icon: "Receipt" }
    ]
  },
  {
    id: "manufacturing_unit",
    name: "Manufacturing Unit",
    badge: "Industry & Production",
    description: "Raw material consumption, WIP, finished goods, factory overheads, labour, machine maintenance.",
    icon: "Factory",
    units: ["KG", "MT", "LITRE", "METRE", "PCS", "NOS"],
    defaultGSTRate: 18,
    valuationMethod: "Standard Costing",
    inventoryGroups: [
      "Raw Materials (Direct Stock)",
      "Work-in-Progress (WIP Under Process)",
      "Finished Goods (Ready for Dispatch)",
      "Packing Materials & Corrugated Boxes",
      "Machine Spares, Oils & Consumables"
    ],
    inventoryCategories: ["Raw Material", "Work in Progress", "Finished Goods", "Packaging"],
    incomeLedgers: [
      { name: "Sales - Finished Goods (Own Manufacture)", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Job Work & Processing Income", group: "Direct Incomes", nature: "Income", type: "Cr" },
      { name: "Sales - Factory Scrap & Residue", group: "Indirect Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Raw Materials Inward", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Primary Packing Materials", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Factory Direct Labour & Wages", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Production workers' wages" },
      { name: "Industrial Power & Electricity (HT)", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Heavy machinery power consumption" },
      { name: "Factory Generator Diesel & Fuel", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Machine Maintenance, Repairs & Lubricants", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Raw Material Inward Freight", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Job Work Charges Paid to Third Parties", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Factory Land & Shed Lease", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Machinery Depreciation", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Factory Insurance (Fire & Plant)", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Quality Assurance & Lab Testing", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "8479", desc: "Machines & Mechanical Appliances", rate: 18 },
      { code: "3923", desc: "Articles for Conveyance or Packing", rate: 18 }
    ],
    nextSteps: [
      { title: "Define Raw Materials & BOM", desc: "Create your raw material and finished goods lists", actionUrl: "/dashboard/inventory", icon: "Boxes" },
      { title: "Log Direct Production Costs", desc: "Record factory electricity and labour expenses", actionUrl: "/dashboard/expenses", icon: "IndianRupee" }
    ]
  },
  {
    id: "construction_materials",
    name: "Construction and Building Materials",
    badge: "Building & Infrastructure",
    description: "Cement, sand, aggregates, bricks, tiles, paint, sanitaryware, site delivery & transit loss.",
    icon: "HardHat",
    units: ["BAG", "TON", "BRASS", "SQFT", "SQMT", "BUCKET", "PCS"],
    defaultGSTRate: 18,
    valuationMethod: "Weighted Average",
    inventoryGroups: [
      "Cement (OPC / PPC / Slag)",
      "Sand, Aggregates & Stone Grit",
      "Bricks, AAC Blocks & Precast",
      "Ceramic Tiles & Sanitaryware",
      "Paints, Primers & Waterproofing",
      "Plumbing Pipes, CPVC & Fittings"
    ],
    inventoryCategories: ["Civil Supplies", "Tiles & Sanitary", "Paints & Waterproofing"],
    incomeLedgers: [
      { name: "Sales - Building Materials", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Site Transport & Offloading Income", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Cement Companies (UltraTech/ACC)", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Quarry & Sand Suppliers", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Truck Freight & Demurrage Charges", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Construction Site Unloading Labour", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Tile & Ceramic Breakage Losses", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Material Depot Yard Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Depot Crane & Loader Fuel", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "2523", desc: "Portland Cement (28% GST)", rate: 28 },
      { code: "6907", desc: "Ceramic Flags & Paving Tiles", rate: 18 },
      { code: "3209", desc: "Paints and Varnishes", rate: 18 }
    ],
    nextSteps: [
      { title: "Add Cement & Quarry Vendors", desc: "Configure direct mill accounts with HSN 2523", actionUrl: "/dashboard/parties", icon: "Users" },
      { title: "Set Up Site-Wise Delivery Invoices", desc: "Issue delivery challans with site address details", actionUrl: "/dashboard/sales", icon: "Truck" }
    ]
  },
  {
    id: "automobile_spares",
    name: "Automobile and Spare Parts",
    badge: "Auto & Mobility",
    description: "Two-wheeler, four-wheeler & commercial auto parts, lubricants, batteries, garage labour.",
    icon: "Car",
    units: ["PCS", "SET", "LITRE", "CAN", "PAIR", "NOS"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Engine & Transmission Components",
      "Brake Discs, Pads & Shoes",
      "Suspension, Shockers & Steering",
      "Automotive Lubricants, Oils & Coolants",
      "Auto Electricals, Starter & Horns",
      "Batteries, Tyres & Tubes"
    ],
    inventoryCategories: ["Engine & Powertrain", "Brakes & Suspension", "Oils & Fluids", "Electricals"],
    incomeLedgers: [
      { name: "Sales - Auto Spares & Parts", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Automobile Repair & Servicing Labour", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - OEM Genuine Spares", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Lubricants & Greases", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Mechanic & Service Helper Wages", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Used Oil Disposal & Hazard Compliance", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Workshop Consumables (Cotton Waste, Cleaners)", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Workshop & Showroom Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Compressor & Lift Electricity", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "8708", desc: "Parts and Accessories of Motor Vehicles", rate: 28 },
      { code: "2710", desc: "Lubricating Oils & Greases", rate: 18 },
      { code: "8507", desc: "Electric Storage Batteries", rate: 28 }
    ],
    nextSteps: [
      { title: "Index Part Numbers & Brands", desc: "Add manufacturer part numbers (Bosch, Denso, Castrol)", actionUrl: "/dashboard/inventory", icon: "Cpu" },
      { title: "Generate Garage Job Cards & Invoices", desc: "Bill parts plus service labour in single invoice", actionUrl: "/dashboard/sales", icon: "Wrench" }
    ]
  },
  {
    id: "pharmacy_healthcare",
    name: "Pharmacy and Healthcare Retail",
    badge: "Health & Pharma",
    description: "Prescription drugs, OTC, surgicals, batch & expiry management, cold storage tracking.",
    icon: "Pill",
    units: ["STRIP", "BOX", "BOTTLE", "VIAL", "PCS", "TUBE"],
    defaultGSTRate: 12,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Prescription Tablets, Capsules & Syrups",
      "Over-The-Counter (OTC) & First Aid",
      "Surgical Disposables, Syringes & Bandages",
      "Baby Care, Diapers & Infant Formula",
      "Nutritional Supplements & Ayurvedic",
      "Medical Diagnostic Equipment (BP, Gluco)"
    ],
    inventoryCategories: ["Prescription Drugs", "OTC & Wellness", "Surgicals"],
    incomeLedgers: [
      { name: "Sales - Medicines & Formulations", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Healthcare & Surgical Items", group: "Sales Accounts", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Pharma C&F & Stockists", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Near-Expiry Return Loss / Adjustments", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Bio-medical Waste Disposal Fees", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Cold Storage Refrigerator Backup Fuel", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Pharmacy Store Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Registered Pharmacist Salary", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Drug License Renewal & FDA Fees", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "3004", desc: "Medicaments for Therapeutic Uses", rate: 12 },
      { code: "9018", desc: "Medical & Surgical Instruments", rate: 12 }
    ],
    nextSteps: [
      { title: "Enable Batch Number & Expiry Tracking", desc: "Configure batch numbers for strict drug compliance", actionUrl: "/dashboard/inventory", icon: "Calendar" },
      { title: "Set Up Doctor / Prescription Counter", desc: "Fast billing with doctor name prefill", actionUrl: "/dashboard/sales", icon: "UserCheck" }
    ]
  },
  {
    id: "ecommerce_online",
    name: "E-commerce and Online Sellers",
    badge: "Digital Commerce",
    description: "Amazon, Flipkart, Shopify, D2C, marketplace commissions, courier charges, RTO returns & TCS under GST.",
    icon: "Globe",
    units: ["PCS", "PACK", "BOX", "SET"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "D2C Brand Finished Stock",
      "Amazon FBA / Flipkart Fulfilled Stock",
      "Courier Envelopes & Bubble Packaging",
      "RTO Returns in Transit"
    ],
    inventoryCategories: ["Online Inventory", "Packaging Supplies"],
    incomeLedgers: [
      { name: "Sales - Amazon Marketplace", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Flipkart Marketplace", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Website / D2C (Shopify)", group: "Sales Accounts", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Trade Inventory for E-com", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Courier Boxes & Polybags", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Marketplace Commission & Referral Fees", group: "Direct Expenses", nature: "Expense", type: "Dr", description: "Amazon/Flipkart seller fees" },
      { name: "Courier & Shipping Expenses (Delhivery/Bluedart)", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Payment Gateway Fee (Razorpay/Cashfree)", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Customer Returns & RTO Damage Loss", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Performance Marketing Ads (Meta / Google)", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Product Photoshoot & Video Production", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Shopify / SaaS Software Subscriptions", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "6307", desc: "Made up Textile Articles", rate: 12 },
      { code: "3926", desc: "Other Articles of Plastics", rate: 18 }
    ],
    nextSteps: [
      { title: "Review Marketplace TCS Accounts", desc: "Check TCS receivable under GST Section 52", actionUrl: "/dashboard/gst", icon: "Shield" },
      { title: "Connect Shipping Accounts", desc: "Automate courier expense reconciliation", actionUrl: "/dashboard/expenses", icon: "Truck" }
    ]
  },
  {
    id: "professional_services",
    name: "Professional Services (CA, Legal, Consulting)",
    badge: "Services & Advisory",
    description: "CA firms, legal advisors, architects, business consultants, retainers, hourly billing & client expenses.",
    icon: "Briefcase",
    units: ["HOURS", "DAYS", "SESSIONS", "PROJECTS", "MONTHS"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Taxation & GST Compliance Packages",
      "Statutory Audit & Certification Services",
      "Corporate Legal & ROC Retainers",
      "Business Advisory & Valuation Sprints"
    ],
    inventoryCategories: ["Audit & Assurance", "Taxation", "Legal Advisory", "Consulting"],
    incomeLedgers: [
      { name: "Professional Audit & Certification Fees", group: "Direct Incomes", nature: "Income", type: "Cr" },
      { name: "Monthly Retainer Advisory Income", group: "Direct Incomes", nature: "Income", type: "Cr" },
      { name: "Client Reimbursable Expenses Recovered", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [],
    directExpenses: [
      { name: "Sub-Contractor & Freelance Consultant Fees", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Article Assistant & Trainee Stipends", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Filing & Government ROC Portal Fees", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Office Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "ICAI / Bar Council Membership Fees", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Legal Books & Database Subscriptions", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Professional Indemnity Insurance", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "9982", desc: "Legal & Accounting Services", rate: 18 },
      { code: "9983", desc: "Consulting & Management Services", rate: 18 }
    ],
    nextSteps: [
      { title: "Set Up Retainer Billing", desc: "Create recurring service retainers for monthly clients", actionUrl: "/dashboard/sales", icon: "Clock" },
      { title: "Track TDS Receivable (Sec 194J)", desc: "Monitor TDS deducted by clients on professional invoices", actionUrl: "/dashboard/ledgers", icon: "FileText" }
    ]
  },
  {
    id: "digital_services",
    name: "Digital Services & Marketing",
    badge: "Digital & Media",
    description: "Digital marketing agencies, SEO, content creators, media buying, client ad budgets & SaaS tools.",
    icon: "Megaphone",
    units: ["MONTHS", "CAMPAIGNS", "DELIVERABLES", "HOURS"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "SEO & Organic Growth Retainers",
      "Performance Marketing Ad Retainers",
      "Video & Creative Content Deliverables",
      "Influencer Management Packages"
    ],
    inventoryCategories: ["SEO", "Performance Marketing", "Content Creation"],
    incomeLedgers: [
      { name: "Revenue - Agency Retainers", group: "Direct Incomes", nature: "Income", type: "Cr" },
      { name: "Revenue - Ad Management Fees", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [],
    directExpenses: [
      { name: "Freelancer & Creator Payouts", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Client Reimbursable Ad Spend (Meta/Google)", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Stock Media & Licensing Fees", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "SaaS Tools & Analytics (Ahrefs/SEMrush/Canva)", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Coworking Space / Office Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Team Laptops & Tech Depreciation", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "998361", desc: "Advertising & Media Buying Services", rate: 18 },
      { code: "998313", desc: "IT & Digital Infrastructure Consulting", rate: 18 }
    ],
    nextSteps: [
      { title: "Create Client Retainer Profiles", desc: "Add agency clients with SAC 9983 for GST invoices", actionUrl: "/dashboard/parties", icon: "Users" },
      { title: "Set Up Expense Tracking for SaaS Tools", desc: "Add recurring software tools and foreign remittance taxes", actionUrl: "/dashboard/expenses", icon: "Cloud" }
    ]
  },
  {
    id: "it_software",
    name: "IT and Software Services",
    badge: "Technology",
    description: "Software development, SaaS, cloud infrastructure resale, export invoices under LUT & foreign exchange.",
    icon: "Code",
    units: ["HOURS", "MONTHS", "SPRINTS", "LICENSES", "USERS"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Custom Software Development Sprints",
      "Cloud Infrastructure Resale (AWS/Azure/GCP)",
      "SaaS Subscription Seats",
      "Annual Maintenance Contracts (AMC)"
    ],
    inventoryCategories: ["Software Development", "Cloud Resale", "AMC Support"],
    incomeLedgers: [
      { name: "Revenue - Domestic Software Development", group: "Direct Incomes", nature: "Income", type: "Cr" },
      { name: "Revenue - Export of Services (Under LUT - Zero Rated)", group: "Direct Incomes", nature: "Income", type: "Cr" },
      { name: "Revenue - AMC & Support Contracts", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Cloud Partner Resale Costs (AWS/Microsoft)", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Developer & Contractor Payouts", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Cloud Servers & Hosting Infrastructure", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Software Licenses & API Usage", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Office Lease & High-Speed Internet", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Foreign Inward Remittance Bank Charges", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Foreign Exchange Fluctuation (Forex Gain/Loss)", group: "Indirect Incomes", nature: "Income", type: "Cr" },
    ],
    popularHSN: [
      { code: "998314", desc: "Information Technology Design & Dev", rate: 18 },
      { code: "998315", desc: "Hosting & IT Infrastructure Provisioning", rate: 18 }
    ],
    nextSteps: [
      { title: "Configure Export of Services (LUT)", desc: "Set up zero-rated invoicing for US/Europe foreign clients", actionUrl: "/dashboard/gst", icon: "Globe" },
      { title: "Track Developer Contractor Withholding", desc: "Manage TDS Section 194C/194J on tech sub-contracts", actionUrl: "/dashboard/ledgers", icon: "UserCheck" }
    ]
  },
  {
    id: "restaurants_food",
    name: "Restaurants and Food Businesses",
    badge: "Hospitality & Dining",
    description: "Cafes, dine-in, cloud kitchens, Zomato/Swiggy reconciliation, commercial LPG & fresh produce.",
    icon: "Utensils",
    units: ["KG", "GRAM", "LITRE", "PCS", "PORTION", "BOTTLE", "PACKET"],
    defaultGSTRate: 5,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Fresh Vegetables, Fruits & Greens",
      "Dairy, Paneer, Cheese & Butter",
      "Poultry, Meat, Seafood & Eggs",
      "Dry Provisions, Spices & Flour",
      "Sauces, Condiments & Oils",
      "Takeaway Containers, Cutlery & Bags"
    ],
    inventoryCategories: ["Perishables", "Dairy & Meat", "Dry Stores", "Packaging"],
    incomeLedgers: [
      { name: "Sales - Dine-In Dining (5% GST No ITC)", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Zomato / Swiggy Delivery", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Takeaway & Counter", group: "Sales Accounts", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Raw Food Materials & Groceries", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Meat, Poultry & Dairy", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Commercial LPG Gas Cylinders", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Kitchen Chef & Cook Wages", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Zomato / Swiggy Platform Commissions", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Kitchen Food Wastage & Spoilage", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Food Delivery Packaging Materials", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Restaurant Premises Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Dining Hall & Kitchen Electricity", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "FSSAI License & Health Inspection Fees", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "996331", desc: "Restaurant & Catering Services (5%)", rate: 5 }
    ],
    nextSteps: [
      { title: "Review 5% GST Restaurant Scheme", desc: "Ensure output tax is 5% without input tax credit restriction", actionUrl: "/dashboard/gst", icon: "Percent" },
      { title: "Record Daily Zomato / Swiggy Payouts", desc: "Reconcile food delivery aggregator deductions", actionUrl: "/dashboard/sales", icon: "Receipt" }
    ]
  },
  {
    id: "import_export",
    name: "Import and Export",
    badge: "Foreign Trade",
    description: "Exim firms, customs clearance, shipping lines, port charges, letters of credit (LC) & forex accounts.",
    icon: "Ship",
    units: ["MT", "KG", "CBM", "CONTAINER", "PCS"],
    defaultGSTRate: 18,
    valuationMethod: "Weighted Average",
    inventoryGroups: [
      "Export Oriented Merchandises",
      "Imported Raw Materials (Customs Cleared)",
      "Bonded Warehouse Materials",
      "Export Packing & Pallets"
    ],
    inventoryCategories: ["Export Goods", "Import Materials"],
    incomeLedgers: [
      { name: "Sales - Export (Zero Rated with LUT)", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Sales - Domestic Market (Ex-Import)", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Duty Drawback & RoDTEP Incentive", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase - Import CIF (Overseas Supplier)", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
      { name: "Purchase - Domestic Procurement for Export", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Customs Basic Duty & Surcharge Paid", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Ocean & Air Cargo Freight", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Customs House Agent (CHA) Charges", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Port Handling & Terminal THC Charges", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "ECGC Export Credit Insurance Premium", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Forex Gain / Loss (Currency Fluctuations)", group: "Indirect Incomes", nature: "Income", type: "Cr" },
      { name: "Bank LC & Inward Remittance Charges", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Inspection & Phytosanitary Certification", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "996511", desc: "Freight Transportation by Sea", rate: 18 },
      { code: "996719", desc: "Cargo Handling & Port Services", rate: 18 }
    ],
    nextSteps: [
      { title: "Record Letter of Undertaking (LUT)", desc: "Add your annual GST LUT ARN for duty-free exports", actionUrl: "/dashboard/gst", icon: "FileText" },
      { title: "Track Inward Forex Bank Entries", desc: "Reconcile export invoices with foreign bank FIRC", actionUrl: "/dashboard/journals", icon: "DollarSign" }
    ]
  },
  {
    id: "general_business",
    name: "General / Other Business",
    badge: "Versatile",
    description: "Standard business setup with universal chart of accounts, standard voucher types, and flexible groups.",
    icon: "Briefcase",
    units: ["PCS", "KG", "BOX", "SET", "NOS", "UNIT"],
    defaultGSTRate: 18,
    valuationMethod: "FIFO",
    inventoryGroups: [
      "Standard Products & Stock",
      "Trade Supplies & Goods",
      "General Consumables"
    ],
    inventoryCategories: ["General Goods", "Supplies"],
    incomeLedgers: [
      { name: "Sales Account - Primary", group: "Sales Accounts", nature: "Income", type: "Cr" },
      { name: "Other Operating Income", group: "Direct Incomes", nature: "Income", type: "Cr" },
    ],
    purchaseLedgers: [
      { name: "Purchase Account - Primary", group: "Purchase Accounts", nature: "Expense", type: "Dr" },
    ],
    directExpenses: [
      { name: "Freight Inward", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Direct Labour Charges", group: "Direct Expenses", nature: "Expense", type: "Dr" },
      { name: "Packaging Expenses", group: "Direct Expenses", nature: "Expense", type: "Dr" },
    ],
    indirectExpenses: [
      { name: "Office Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Electricity & Utilities", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Staff Salaries", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
      { name: "Bank Charges", group: "Indirect Expenses", nature: "Expense", type: "Dr" },
    ],
    popularHSN: [
      { code: "9983", desc: "Other Commercial Services", rate: 18 }
    ],
    nextSteps: [
      { title: "Add Your First Client", desc: "Save your frequent customers and buyers", actionUrl: "/dashboard/parties", icon: "Users" },
      { title: "Create Your First Invoice", desc: "Issue a professional sales invoice", actionUrl: "/dashboard/sales", icon: "Receipt" }
    ]
  }
];

export const DEFAULT_VOUCHER_TYPES = [
  "Sales",
  "Purchase",
  "Payment",
  "Receipt",
  "Journal",
  "Contra",
  "Debit Note",
  "Credit Note",
];

export const INDUSTRY_CATEGORIES = [
  "All",
  "Manufacturing & Heavy",
  "Retail & Wholesale",
  "Tech & Services",
  "Food & Hospitality",
  "Healthcare & Essentials",
];

function mapBadgeToCategory(badge: string): string {
  if (badge.includes("Industrial") || badge.includes("Manufacturing") || badge.includes("Construction") || badge.includes("Recycling")) {
    return "Manufacturing & Heavy";
  }
  if (badge.includes("Food") || badge.includes("Hospitality")) {
    return "Food & Hospitality";
  }
  if (badge.includes("Health") || badge.includes("Pharma") || badge.includes("Beauty") || badge.includes("FMCG")) {
    return "Healthcare & Essentials";
  }
  if (badge.includes("Tech") || badge.includes("IT") || badge.includes("Digital") || badge.includes("Services")) {
    return "Tech & Services";
  }
  return "Retail & Wholesale";
}

// Enrich templates with unified accessor properties
const ENRICHED_TEMPLATES = RAW_INDUSTRY_TEMPLATES.map((t) => {
  const category = mapBadgeToCategory(t.badge);
  return {
    ...t,
    category,
    defaultUnits: t.units,
    stockValuationMethod: t.valuationMethod,
    stockGroups: t.inventoryGroups,
    stockCategories: t.inventoryCategories,
    defaultVoucherTypes: DEFAULT_VOUCHER_TYPES,
    tags: [t.badge, ...t.units.slice(0, 2), ...t.inventoryGroups.slice(0, 2)],
  };
});

export const INDUSTRY_TEMPLATES = ENRICHED_TEMPLATES;
export const ALL_INDUSTRY_TEMPLATES = ENRICHED_TEMPLATES;

export function getIndustryTemplateById(id: string) {
  const found = ENRICHED_TEMPLATES.find((t) => t.id === id);
  return found || ENRICHED_TEMPLATES[ENRICHED_TEMPLATES.length - 1];
}

export const getIndustryTemplate = getIndustryTemplateById;

/**
 * Generates the complete, customized Chart of Accounts for a company
 * based on its Industry Template, Business Constitution, and GST Status.
 */
export function buildChartOfAccounts(
  template: IndustryTemplate,
  constitution: string,
  isGSTRegistered: boolean
): LedgerTemplateItem[] {
  const ledgers: LedgerTemplateItem[] = [];

  // 1. Capital / Equity (Customized by Constitution)
  if (constitution === "Proprietorship") {
    ledgers.push({ name: "Proprietor's Capital Account", group: "Capital Account", nature: "Equity", type: "Cr", description: "Owner's capital investment" });
    ledgers.push({ name: "Proprietor's Drawings Account", group: "Capital Account", nature: "Equity", type: "Dr", description: "Personal withdrawals by owner" });
  } else if (constitution === "Partnership" || constitution === "LLP") {
    ledgers.push({ name: "Partner's Capital Account - 1", group: "Capital Account", nature: "Equity", type: "Cr", description: "Primary partner's capital" });
    ledgers.push({ name: "Partner's Current Account", group: "Capital Account", nature: "Equity", type: "Cr", description: "Partner profit share and drawings" });
  } else {
    ledgers.push({ name: "Equity Share Capital", group: "Capital Account", nature: "Equity", type: "Cr", description: "Authorized & paid-up share capital" });
    ledgers.push({ name: "Retained Earnings / Surplus", group: "Capital Account", nature: "Equity", type: "Cr", description: "Accumulated profits" });
  }

  // 2. Liquid Assets & Banking
  ledgers.push({ name: "Cash-in-Hand", group: "Cash-in-Hand", nature: "Asset", type: "Dr", description: "Physical store or petty cash" });
  ledgers.push({ name: "Primary Bank Account (Current A/c)", group: "Bank Accounts", nature: "Asset", type: "Dr", description: "Main operational business bank account" });

  // 3. Trade Debtors & Creditors
  ledgers.push({ name: "Sundry Debtors (Customers)", group: "Sundry Debtors", nature: "Asset", type: "Dr", description: "Receivables from clients and buyers" });
  ledgers.push({ name: "Sundry Creditors (Suppliers)", group: "Sundry Creditors", nature: "Liability", type: "Cr", description: "Payables to vendors and suppliers" });

  // 4. Industry Specific Incomes
  for (const inc of template.incomeLedgers) {
    ledgers.push({ ...inc });
  }

  // 5. Industry Specific Purchases
  for (const pur of template.purchaseLedgers) {
    ledgers.push({ ...pur });
  }

  // 6. Industry Specific Direct Expenses
  for (const de of template.directExpenses) {
    ledgers.push({ ...de });
  }

  // 7. Universal & Industry Specific Indirect Expenses
  ledgers.push({ name: "Office / Shop Rent", group: "Indirect Expenses", nature: "Expense", type: "Dr" });
  ledgers.push({ name: "Staff Salaries & Wages", group: "Indirect Expenses", nature: "Expense", type: "Dr" });
  ledgers.push({ name: "Electricity & Utility Bills", group: "Indirect Expenses", nature: "Expense", type: "Dr" });
  ledgers.push({ name: "Bank Charges & Transaction Fees", group: "Indirect Expenses", nature: "Expense", type: "Dr" });
  ledgers.push({ name: "Audit & Legal Compliance Fees", group: "Indirect Expenses", nature: "Expense", type: "Dr" });
  ledgers.push({ name: "Discount Allowed", group: "Indirect Expenses", nature: "Expense", type: "Dr" });
  ledgers.push({ name: "Round Off", group: "Indirect Expenses", nature: "Expense", type: "Dr" });

  for (const ie of template.indirectExpenses) {
    if (!ledgers.some((l) => l.name.toLowerCase() === ie.name.toLowerCase())) {
      ledgers.push({ ...ie });
    }
  }

  // 8. Indirect Incomes
  ledgers.push({ name: "Discount Received", group: "Indirect Incomes", nature: "Income", type: "Cr" });
  ledgers.push({ name: "Bank Interest Income", group: "Indirect Incomes", nature: "Income", type: "Cr" });

  // 9. Statutory Duties & Taxes (ONLY if GST Registered!)
  if (isGSTRegistered) {
    ledgers.push({ name: "CGST Output (Sales)", group: "Duties & Taxes", nature: "Liability", type: "Cr", description: "Central GST liability on intra-state sales" });
    ledgers.push({ name: "SGST Output (Sales)", group: "Duties & Taxes", nature: "Liability", type: "Cr", description: "State GST liability on intra-state sales" });
    ledgers.push({ name: "IGST Output (Sales)", group: "Duties & Taxes", nature: "Liability", type: "Cr", description: "Integrated GST liability on inter-state sales" });
    ledgers.push({ name: "CGST Input (ITC)", group: "Duties & Taxes", nature: "Asset", type: "Dr", description: "Central GST input tax credit on purchases" });
    ledgers.push({ name: "SGST Input (ITC)", group: "Duties & Taxes", nature: "Asset", type: "Dr", description: "State GST input tax credit on purchases" });
    ledgers.push({ name: "IGST Input (ITC)", group: "Duties & Taxes", nature: "Asset", type: "Dr", description: "Integrated GST input tax credit on purchases" });
    ledgers.push({ name: "RCM Tax Payable", group: "Duties & Taxes", nature: "Liability", type: "Cr", description: "Reverse charge liability on unregistered purchases" });
    ledgers.push({ name: "TDS Payable", group: "Duties & Taxes", nature: "Liability", type: "Cr", description: "Tax deducted at source on contractor/rent payments" });
  }

  return ledgers;
}
